import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Panel } from "components/panels";
import TwoColumnLayout from "layout/TwoColumnLayout";
import { SearchInput } from "components/Atoms/SearchInput";
import React from "react";
import CategorySidebar from "./CategorySidebar";
import { CategoryFilter } from "./types";
import { useDebounce } from "utilities/usehooks";
import { ButtonPrimary } from "components/Atoms/Button";
import ArticleRow from "./ArticleRow";
import { routes } from "utilities/routing/generatedRoutes";
import { useLoaderData } from "react-router";

const ArticleList = () => {
  const viewModel = useLoaderData<server.viewmodel.ArticleListFrontModel>();

  const articleList = useQuery({
    queryFn: () => window.CoreApi.Article.Paginated({
      page: 1,
      perPage: 999,
      sort: "internalTitle",
      published: "noFilter"
    }, viewModel.section).then(r => r.data),
    queryKey: ["allArticles", viewModel.section]
  });
  const queryClient = useQueryClient();

  const invalidateQuery = () => queryClient.invalidateQueries(["allArticles", viewModel.section]);

  const [categoryFilter, setCategoryFilter] = React.useState<CategoryFilter>("noFilter");
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const debouncedSearchQuery = useDebounce(searchQuery);

  if (articleList.isLoading) {
    return <TwoColumnLayout>
      <TwoColumnLayout.LeftColumn>
        <Panel panelClassName="s1_listview-panel" title={viewModel.pageTitle} headerSize="h1" isLoading></Panel>
      </TwoColumnLayout.LeftColumn>
    </TwoColumnLayout>
  }

  const allArticles = articleList.data.data ?? [];
  const filteredArticles = allArticles
    .filter(a => {
      if (categoryFilter === "noFilter") {
        return true;
      }

      if (categoryFilter === "noCategory") {
        return a.categories.length === 0;
      }

      return a.categories.some(c => c.articleCategoryId === categoryFilter);
    })
    .filter(a => a.internalTitle.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));

  const onNewArticleClicked = async () => {
    const res = await window.CoreApi.Article.Create({ section: viewModel.section });

    if (res.data.hasErrors) {
      window.displayGenericModal("Error", res.data.errors.map(e => e.message).join("\n"), "mini");
      return;
    }

    window.location.href = routes.article_Detail(res.data.data).absolute;
  }

  const onDeleteArticleClicked = (article: server.dto.Article) => {
    window.displayDeleteConfirmationModal({
      onDeleteHandler: async () => {
        await window.CoreApi.Article.SoftDelete(article.articleId);
        invalidateQuery();
      }
    });
  }

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <Panel panelClassName="s1_listview-panel" contentCssClass="flex" title={viewModel.pageTitle} headerSize="h1">
        <CategorySidebar section={viewModel.section} allCategories={viewModel.articleCategories} articles={allArticles} onCategoryFilter={setCategoryFilter} />
        <div className="grow1">
          <div className="margin-bottom-huge flex">
            <div className="grow1" style={{ marginRight: 20 }}>
              <SearchInput
                onChange={setSearchQuery}
                value={searchQuery}
              />
            </div>
            <div>
              <ButtonPrimary label={RESX.Buttons.btnNew} onClick={onNewArticleClicked} />
            </div>
          </div>

          <div className="flex-column gap-small grow1">
            {filteredArticles.map(article => <ArticleRow key={article.articleId} article={article} onDelete={onDeleteArticleClicked} />)}
          </div>
        </div>
      </Panel>
    </TwoColumnLayout.LeftColumn>
  </TwoColumnLayout>
}

export default ArticleList;